import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation, useLocale } from "./contexts/LocaleContext";
import { authenticatedFetch } from "./config/api";
import { sanitizeInput } from "./utils/sanitize";
import SaveButton from "./components/SaveButton";
import {
  Trash2,
  Calendar,
  Clock,
  Plus,
  Search,
  ChevronRight,
  AlertCircle,
  Info,
} from "lucide-react";

interface InvoiceListItem {
  id: string;
  internalId?: string;
  invoiceNo: string;
  clientName: string;
  description: string;
  amount: number;
  currency: string;
  issueDate: string;
  dueDate: string;
  status: "paid" | "pending" | "overdue";
  createdAt: string;
}

interface RepeatingTemplate {
  id: string;
  name: string;
  sourceInvoiceId: string;
  repeatType: "days" | "monthly";
  intervalDays?: number;
  dayOfMonth?: number;
  startDate: string;
  endDate: string;
  lastGeneratedDate?: string;
  invoiceNo: string;
  clientName: string;
}

export default function RepeatingInvoices() {
  const t = useTranslation();
  const { locale } = useLocale();

  // Step 1: Selection state
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );

  // Step 2: Configuration state
  const [repeatType, setRepeatType] = useState<"days" | "monthly">("days");
  const [intervalDays, setIntervalDays] = useState<string>("30");
  const [dayOfMonth, setDayOfMonth] = useState<string>("1");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().split("T")[0];
  });
  const [templateName, setTemplateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [successCount, setSuccessCount] = useState<number | null>(null);

  // Management state
  const [templates, setTemplates] = useState<RepeatingTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

  // Load invoices
  const loadInvoices = async () => {
    try {
      setIsLoadingInvoices(true);
      const response = await authenticatedFetch("/api/invoices-full");
      if (response.ok) {
        const fullInvoices = await response.json();
        const convertedInvoices: InvoiceListItem[] = fullInvoices.map((inv: any) => {
          const activeVersion = inv;
          if (activeVersion && activeVersion.draft) {
            const computedAmount = Array.isArray(activeVersion.draft.items)
              ? activeVersion.draft.items.reduce(
                  (sum: number, item: any) =>
                    sum + (item?.amount || 0) * (item?.quantity || 1),
                  0
                )
              : 0;
            return {
              id: inv.internalId,
              internalId: inv.internalId,
              invoiceNo: activeVersion.draft.invoiceNo || "",
              clientName: activeVersion.draft.buyerName || "",
              description:
                (activeVersion.draft.items?.length > 0
                  ? activeVersion.draft.items[0]?.description
                  : "") || "",
              amount: computedAmount,
              currency: activeVersion.draft.currency || "CZK",
              issueDate: activeVersion.draft.issueDate || "",
              dueDate: activeVersion.draft.dueDate || "",
              status: (activeVersion.draft.status || "pending") as any,
              createdAt: inv.createdAt || new Date().toISOString(),
            };
          }
          return null;
        });
        setInvoices(
          convertedInvoices.filter((inv) => inv !== null) as InvoiceListItem[]
        );
      }
    } catch (error) {
      console.error("Error loading invoices:", error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  // Load templates (Mock for now since backend might not have it yet)
  const loadTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      // In a real app, this would be:
      // const response = await authenticatedFetch("/api/repeating-invoices");
      // if (response.ok) setTemplates(await response.json());

      // For now, let's use localStorage to simulate
      const stored = localStorage.getItem("repeating-invoice-templates");
      if (stored) {
        setTemplates(JSON.parse(stored));
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    loadTemplates();
  }, []);

  const filteredInvoices = useMemo(() => {
    if (!searchQuery) return invoices;
    const query = searchQuery.toLowerCase();
    return invoices.filter(
      (inv) =>
        inv.invoiceNo.toLowerCase().includes(query) ||
        inv.clientName.toLowerCase().includes(query) ||
        inv.description.toLowerCase().includes(query) ||
        inv.issueDate.toLowerCase().includes(query)
    );
  }, [invoices, searchQuery]);

  const handleCreateTemplate = async () => {
    if (!selectedInvoiceId) return;

    setIsCreating(true);
    setSuccessCount(null);

    try {
      const parsedIntervalDays = parseInt(intervalDays);
      const parsedDayOfMonth = parseInt(dayOfMonth);

      // Validate inputs
      if (
        repeatType === "days" &&
        (isNaN(parsedIntervalDays) || parsedIntervalDays < 1)
      ) {
        throw new Error("Invalid interval days");
      }
      if (
        repeatType === "monthly" &&
        (isNaN(parsedDayOfMonth) ||
          parsedDayOfMonth < 1 ||
          parsedDayOfMonth > 31)
      ) {
        throw new Error("Invalid day of month");
      }

      // 1. Fetch full details of the source invoice
      const fullResponse = await authenticatedFetch(
        `/api/invoices/${selectedInvoiceId}`
      );
      if (!fullResponse.ok) throw new Error("Failed to fetch source invoice");

      const fullData = await fullResponse.json();
      const activeVersion = fullData.versions?.find(
        (v: any) => v.metadata.isActive
      );
      if (!activeVersion) throw new Error("No active version found");

      const sourceDraft = activeVersion.draft;
      const sourceIssueDate = new Date(sourceDraft.issueDate);
      const sourceDueDate = new Date(sourceDraft.dueDate);

      // Calculate due days interval (e.g., 14 days)
      const dueDaysDiff = Math.round(
        (sourceDueDate.getTime() - sourceIssueDate.getTime()) /
          (1000 * 60 * 60 * 24)
      );

      // 2. Calculate creation plan
      const start = new Date(startDate + "T00:00:00");
      const end = new Date(endDate + "T23:59:59");
      const datesToCreate: Date[] = [];

      if (repeatType === "days") {
        let current = new Date(start);
        while (current <= end) {
          datesToCreate.push(new Date(current));
          current.setDate(current.getDate() + parsedIntervalDays);
          if (parsedIntervalDays <= 0) break;
        }
      } else {
        // Monthly logic
        // We need to be careful with the starting point
        let year = start.getFullYear();
        let month = start.getMonth();

        // Safety counter to prevent infinite loops
        let safety = 0;
        while (safety < 1200) {
          safety++;

          const lastDayOfCurrentMonth = new Date(year, month + 1, 0).getDate();
          const targetDay = Math.min(parsedDayOfMonth, lastDayOfCurrentMonth);
          const issueDate = new Date(year, month, targetDay, 0, 0, 0);

          const moveToNext = () => {
            month++;
            if (month > 11) {
              month = 0;
              year++;
            }
          };

          if (issueDate > end) break;

          if (issueDate >= start) {
            datesToCreate.push(new Date(issueDate));
          }

          moveToNext();
        }
      }

      // 3. Prepare invoices for batch save
      const invoicesToSave = datesToCreate.map((issueDate) => {
        // Ensure we use local date components for the string representation
        const y = issueDate.getFullYear();
        const m = String(issueDate.getMonth() + 1).padStart(2, "0");
        const d = String(issueDate.getDate()).padStart(2, "0");
        const issueDateStr = `${y}-${m}-${d}`;

        const newDueDate = new Date(issueDate);
        newDueDate.setDate(issueDate.getDate() + dueDaysDiff);

        const dy = newDueDate.getFullYear();
        const dm = String(newDueDate.getMonth() + 1).padStart(2, "0");
        const dd = String(newDueDate.getDate()).padStart(2, "0");
        const dueDateStr = `${dy}-${dm}-${dd}`;

        return {
          draft: {
            ...sourceDraft,
            issueDate: issueDateStr,
            dueDate: dueDateStr,
            status: "pending",
          },
          designSettings: activeVersion.designSettings,
          language: activeVersion.language,
        };
      });

      // Check limit
      if (invoicesToSave.length > 300) {
        throw new Error("Batch size limit exceeded (max 300)");
      }

      // 4. Batch save invoices
      const batchResponse = await authenticatedFetch(
        "/api/invoices/batch-save",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            invoices: invoicesToSave,
          }),
        }
      );

      if (!batchResponse.ok) {
        const errorData = await batchResponse.json();
        throw new Error(errorData.error || "Failed to batch save invoices");
      }

      const batchResult = await batchResponse.json();
      const createdCount = batchResult.count || 0;

      // 5. Update templates list (UI only)
      const selectedInvoice = invoices.find(
        (inv) => inv.id === selectedInvoiceId
      );
      const newTemplate: RepeatingTemplate = {
        id: Math.random().toString(36).substr(2, 9),
        name:
          templateName ||
          (locale === "ru"
            ? `Ежемесячный счет для ${selectedInvoice?.clientName}`
            : locale === "cs"
            ? `Měsíční faktura pro ${selectedInvoice?.clientName}`
            : `Monthly invoice for ${selectedInvoice?.clientName}`),
        sourceInvoiceId: selectedInvoiceId,
        repeatType,
        intervalDays: repeatType === "days" ? parsedIntervalDays : undefined,
        dayOfMonth: repeatType === "monthly" ? parsedDayOfMonth : undefined,
        startDate,
        endDate,
        invoiceNo: selectedInvoice?.invoiceNo || "",
        clientName: selectedInvoice?.clientName || "",
      };

      const updatedTemplates = [...templates, newTemplate];
      setTemplates(updatedTemplates);
      localStorage.setItem(
        "repeating-invoice-templates",
        JSON.stringify(updatedTemplates)
      );

      // 6. Show success and reset
      console.log("Successfully created invoices count:", createdCount);
      setSuccessCount(createdCount);
      setSelectedInvoiceId(null);
      setTemplateName("");

      // Auto-hide success message after 10 seconds (increased from 5)
      setTimeout(() => setSuccessCount(null), 10000);
    } catch (error) {
      console.error("Error creating repeating invoices:", error);
      alert(t.common.error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTemplate = (id: string) => {
    if (window.confirm(t.repeatingInvoices.deleteConfirm)) {
      const updatedTemplates = templates.filter((tpl) => tpl.id !== id);
      setTemplates(updatedTemplates);
      localStorage.setItem(
        "repeating-invoice-templates",
        JSON.stringify(updatedTemplates)
      );
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    if (currency === "CZK") {
      return `${amount.toLocaleString("cs-CZ", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} Kč`;
    }
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "CZK",
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Success Message Banner */}
        <AnimatePresence>
          {successCount !== null && (
            <motion.div
              key="success-banner"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl flex items-center gap-3 text-emerald-800 dark:text-emerald-300 shadow-sm z-50"
            >
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-800/40 rounded-full flex items-center justify-center flex-shrink-0">
                <Plus className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="font-bold">
                {t.repeatingInvoices.successCreated.replace(
                  "{count}",
                  successCount.toString()
                )}
              </p>
              <button
                onClick={() => setSuccessCount(null)}
                className="ml-auto p-2 hover:bg-emerald-100 dark:hover:bg-emerald-800/40 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 opacity-50 hover:opacity-100 rotate-45" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 transition-colors">
            {t.repeatingInvoices.title}
          </h1>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Step 1: Selection */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-xl overflow-hidden p-6"
          >
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 shrink-0 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm">
                1
              </span>
              {t.repeatingInvoices.step1}
            </h2>

            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder={t.invoiceList.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(sanitizeInput(e.target.value))}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all outline-none"
              />
            </div>

            <div className="overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
              {isLoadingInvoices ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : filteredInvoices.length > 0 ? (
                <div className="space-y-2">
                  {filteredInvoices.map((inv) => (
                    <div
                      key={inv.id}
                      onClick={() =>
                        setSelectedInvoiceId((prev) =>
                          prev === inv.id ? null : inv.id
                        )
                      }
                      className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                        selectedInvoiceId === inv.id
                          ? "border-purple-600 bg-purple-50 dark:bg-purple-900/20 shadow-md ring-[1.5px] ring-purple-600"
                          : "border-slate-100 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700 bg-slate-50/50 dark:bg-slate-900/50"
                      }`}
                    >
                      <div className="flex justify-between items-center gap-4">
                        <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                          <div className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                            #{inv.invoiceNo}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                            {new Date(inv.issueDate).toLocaleDateString()}
                          </div>
                          <div
                            className="text-xs text-slate-600 dark:text-slate-300 truncate max-w-[150px]"
                            title={inv.clientName}
                          >
                            {inv.clientName}
                          </div>
                        </div>
                        <div className="text-xs font-black px-2 py-1 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm whitespace-nowrap">
                          {formatAmount(inv.amount, inv.currency)}
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 truncate italic">
                        {inv.description}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-500">
                  {t.common.noInvoicesFound}
                </div>
              )}
            </div>
          </motion.div>

          {/* Step 2: Configuration */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-xl p-6"
          >
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 shrink-0 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm">
                2
              </span>
              {t.repeatingInvoices.step2}
            </h2>

            <div className="space-y-6">
              {/* Option 1: Monthly Day */}
              <div
                className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  repeatType === "monthly"
                    ? "border-purple-600 bg-purple-50 dark:bg-purple-900/20"
                    : "border-slate-100 dark:border-slate-700 hover:border-purple-200 dark:hover:border-purple-800"
                }`}
                onClick={() => setRepeatType("monthly")}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <input
                      type="radio"
                      checked={repeatType === "monthly"}
                      onChange={() => setRepeatType("monthly")}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 transition-colors block mb-2 cursor-pointer">
                      {t.repeatingInvoices.everyMonthDay}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={dayOfMonth}
                        onChange={(e) => setDayOfMonth(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-20 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 transition-all outline-none text-center font-bold ${
                          repeatType === "monthly" &&
                          (parseInt(dayOfMonth) < 1 ||
                            parseInt(dayOfMonth) > 31 ||
                            !dayOfMonth)
                            ? "border-red-500 focus:ring-red-500/50"
                            : "border-slate-200 dark:border-slate-700 focus:ring-purple-500/50"
                        }`}
                      />
                      <span className="text-slate-600 dark:text-slate-400 text-sm">
                        {t.repeatingInvoices.dayOfMonth}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 italic">
                      {t.repeatingInvoices.lastDayNote}
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                {t.repeatingInvoices.or}
              </div>

              {/* Option 2: Days interval */}
              <div
                className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  repeatType === "days"
                    ? "border-purple-600 bg-purple-50 dark:bg-purple-900/20"
                    : "border-slate-100 dark:border-slate-700 hover:border-purple-200 dark:hover:border-purple-800"
                }`}
                onClick={() => setRepeatType("days")}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <input
                      type="radio"
                      checked={repeatType === "days"}
                      onChange={() => setRepeatType("days")}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 transition-colors block mb-2 cursor-pointer">
                      {t.repeatingInvoices.intervalLabel}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="1"
                        value={intervalDays}
                        onChange={(e) => setIntervalDays(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-20 px-3 py-2 rounded-lg border bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 transition-all outline-none text-center font-bold ${
                          repeatType === "days" &&
                          (parseInt(intervalDays) < 1 || !intervalDays)
                            ? "border-red-500 focus:ring-red-500/50"
                            : "border-slate-200 dark:border-slate-700 focus:ring-purple-500/50"
                        }`}
                      />
                      <span className="text-slate-600 dark:text-slate-400 text-sm">
                        {t.repeatingInvoices.days}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-4">
                  <label className="w-12 text-xs font-bold text-slate-500 uppercase tracking-wider">
                    {t.repeatingInvoices.fromDate}
                  </label>
                  <div className="flex-1 max-w-[200px]">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="w-12 text-xs font-bold text-slate-500 uppercase tracking-wider">
                    {t.repeatingInvoices.toDate}
                  </label>
                  <div className="flex-1 max-w-[200px]">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  {t.repeatingInvoices.templateNameLabel}{" "}
                  <span className="text-slate-400 font-normal">
                    {t.repeatingInvoices.optional}
                  </span>
                </label>
                <input
                  type="text"
                  placeholder={t.repeatingInvoices.templateNamePlaceholder}
                  value={templateName}
                  onChange={(e) =>
                    setTemplateName(sanitizeInput(e.target.value))
                  }
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500/50 transition-all outline-none"
                />
              </div>

              <div className="pt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={
                    !selectedInvoiceId ||
                    isCreating ||
                    (repeatType === "monthly" &&
                      (parseInt(dayOfMonth) < 1 ||
                        parseInt(dayOfMonth) > 31 ||
                        !dayOfMonth)) ||
                    (repeatType === "days" &&
                      (parseInt(intervalDays) < 1 || !intervalDays))
                  }
                  onClick={handleCreateTemplate}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold text-lg shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-3"
                >
                  {isCreating ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Plus className="h-6 w-6" />
                  )}
                  {t.repeatingInvoices.createButton}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
